import { motion } from "framer-motion";
import { Rocket, Cpu, Zap, Globe2 } from "lucide-react";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const icons = { portfolio: Rocket, "ai-saas": Cpu, speed: Zap, global: Globe2 };

const WhySybrax = () => {
    const { lang } = useLanguage();
    const c = content[lang].why;

    return (
        <section
            id="why-sybrax"
            data-testid="why-sybrax-section"
            className="relative py-24 sm:py-32 overflow-hidden"
        >
            <div className="pointer-events-none absolute -top-20 left-1/3 w-[420px] h-[420px] rounded-full bg-cyan-500/[0.06] blur-[120px]" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.25em] text-cyan-600 dark:text-cyan-400">
                        <span className="mr-3 h-px w-8 bg-cyan-500/70 dark:bg-cyan-400/60" />
                        {c.label}
                    </span>
                    <h2
                        data-testid="why-sybrax-title"
                        className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
                    >
                        {c.title}
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                        {c.intro}
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                    {c.features.map((f, i) => {
                        const Icon = icons[f.key];
                        return (
                            <motion.div
                                key={f.key}
                                data-testid={`why-card-${i}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.6,
                                    ease: [0.22, 1, 0.36, 1],
                                    delay: i * 0.08,
                                }}
                                className="glass-card p-7 flex flex-col h-full group"
                            >
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/15 to-blue-600/10 border border-cyan-500/30 dark:border-cyan-400/20 flex items-center justify-center mb-5 group-hover:shadow-[0_0_24px_rgba(6,182,212,0.25)] transition-shadow">
                                    <Icon
                                        size={22}
                                        strokeWidth={1.5}
                                        className="text-cyan-600 dark:text-cyan-300"
                                    />
                                </div>
                                <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white leading-snug">
                                    {f.title}
                                </h3>
                                <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed flex-1">
                                    {f.description}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default WhySybrax;
